/* eslint-disable @typescript-eslint/no-unused-vars */
import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import menu from "@/src/menu.json";
import "./sidebar.css";
import { MenuItem as MenuItemType } from "@/src/types/menu.types";
import { encode } from "@/src/utils/coder";

function SidebarItem(
  {
    title,
    item,
    level = 0,
    toggleItem,
    openItems,
    parentId = ""
  }:
    {
      title: string,
      item: MenuItemType,
      level?: number,
      toggleItem: (id: string) => void,
      openItems: Record<string, boolean>,
      parentId?:string
    }
) {
  const id = parentId ? `${parentId}>${title}` : title;

  // Hooks declared unconditionally to satisfy rules of hooks
  // refs and state used by submenu positioning and lifecycle
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const portalRef = useRef<HTMLDivElement | null>(null);
  const [submenuPos, setSubmenuPos] = useState<{ top: number; left: number } | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);

  const paddingClass = level === 0 ? "" : level === 1 ? "ps-5" : `ps-${Math.min(5, level * 2)}`;
  const _children = item.child as Record<string, MenuItemType> | undefined;
  const hasChildren = _children && Object.keys(_children).length > 0;
  const isExpanded = Boolean(openItems[id]);
  const route = item.route?.[0]? item.route[0]+`/?c=${encode(id)}` : undefined;

  // early return for separators (declared after hooks so hooks run unconditionally)
  if (level === 0 && /^-\d+$/.test(title)) {
    return <hr key={id} className="my-3" />;
  }

  const computePos = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const submenuWidth = 240; // matches .submenu-float width
    let left = Math.round(rect.right + 8);
    if (left + submenuWidth > window.innerWidth) {
      left = Math.round(rect.left - submenuWidth - 8);
      if (left < 8) left = 8;
    }
    const top = Math.round(rect.top);
    setSubmenuPos({ top, left });
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!isExpanded) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSubmenuPos(null);
      return;
    }

    computePos();
    const onResize = () => computePos();
    const onScroll = () => computePos();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);

    // pointer enter/leave handlers attached after mount to avoid reading refs during render
    const onEnter = () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current as number);
        closeTimeoutRef.current = null;
      }
    };

    const isInsidePortalDescendant = (el: Element | null) => {
      if (!el) return false;
      let node: Element | null = el as Element | null;
      while (node && node !== document.body) {
        const owner = (node as HTMLElement).dataset?.menuOwner;
        if (owner) {
          // owner equal this id => it's the same menu; owner startsWith id+">" => descendant
          if (owner === id || owner.startsWith(id + ">")) return true;
        }
        node = node.parentElement;
      }
      return false;
    };

    const onLeave = (ev?: PointerEvent) => {
      // Prefer using the event coordinates to query what's currently under the pointer.
      try {
        const x = ev?.clientX ?? null;
        const y = ev?.clientY ?? null;
        let elUnderPointer: Element | null = null;
        if (x !== null && y !== null) {
          elUnderPointer = document.elementFromPoint(x, y) as Element | null;
        }
        // If pointer moved into the toggle button, consider it inside
        if (elUnderPointer && btnRef.current && btnRef.current.contains(elUnderPointer)) return;
        // If pointer moved into any portal whose owner is this id or a descendant of this id, consider it inside
        if (elUnderPointer && isInsidePortalDescendant(elUnderPointer)) return;

        // Fallback: if relatedTarget exists and is inside portal descendant, keep open
        const related = ev?.relatedTarget as Node | null;
        if (related) {
          if (btnRef.current && btnRef.current.contains(related)) return;
          if (related instanceof Element && isInsidePortalDescendant(related)) return;
        }
      } catch (_) {
        // elementFromPoint can throw in some environments; ignore and proceed to close
      }

      // schedule close for this id only
      closeTimeoutRef.current = window.setTimeout(() => {
        if (isExpanded) toggleItem(id);
        closeTimeoutRef.current = null;
      }, 300);
    };

    const btn = btnRef.current;
    const portalEl = portalRef.current;

    if (btn) {
      btn.addEventListener("pointerenter", onEnter);
      btn.addEventListener("pointerleave", onLeave as (ev?: PointerEvent) => void);
    }
    if (portalEl) {
      portalEl.addEventListener("pointerenter", onEnter);
      portalEl.addEventListener("pointerleave", onLeave as (ev?: PointerEvent) => void);
    }

    // Enhanced pointer move watcher: cancel close if pointer is currently over this menu's button/portal or any descendant portal
    const onMove = (ev: PointerEvent) => {
      try {
        const x = ev.clientX;
        const y = ev.clientY;
        if (typeof x !== 'number' || typeof y !== 'number') return;
        const el = document.elementFromPoint(x, y) as Element | null;
        if (!el) return;

        // if pointer over toggle button, keep open
        if (btnRef.current && btnRef.current.contains(el)) {
          if (closeTimeoutRef.current) { clearTimeout(closeTimeoutRef.current as number); closeTimeoutRef.current = null; }
          return;
        }

        // if pointer over a portal that belongs to this id or a descendant, keep open
        const ownerEl = el.closest('[data-menu-owner]') as HTMLElement | null;
        if (ownerEl) {
          const owner = ownerEl.dataset.menuOwner;
          if (owner === id || owner?.startsWith(id + ">")) {
            if (closeTimeoutRef.current) { clearTimeout(closeTimeoutRef.current as number); closeTimeoutRef.current = null; }
            return;
          }
        }

        // if pointer over another toggle that is a descendant, keep open
        const toggleEl = el.closest('[data-menu-toggle]') as HTMLElement | null;
        if (toggleEl) {
          const t = toggleEl.dataset.menuToggle;
          if (t === id || t?.startsWith(id + ">")) {
            if (closeTimeoutRef.current) { clearTimeout(closeTimeoutRef.current as number); closeTimeoutRef.current = null; }
            return;
          }
        }
      } catch (_) {
        // ignore
      }
    };

    document.addEventListener('pointermove', onMove);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      if (btn) {
        btn.removeEventListener("pointerenter", onEnter);
        btn.removeEventListener("pointerleave", onLeave as (ev?: PointerEvent) => void);
      }
      if (portalEl) {
        portalEl.removeEventListener("pointerenter", onEnter);
        portalEl.removeEventListener("pointerleave", onLeave as (ev?: PointerEvent) => void);
      }
      document.removeEventListener('pointermove', onMove);
      // clear any pending close timeout when effect cleans up/unmounts
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current as number);
        closeTimeoutRef.current = null;
      }
    };
  }, [isExpanded, id, toggleItem]);

  const dClassNAme = 'sidebar-menu-item px-3 py-2 my-1'
  const textTitle =
    level === 0 && item.icon ?
      <><i className={`${item.icon} me-2`} />
        <span className="w-100">{title}</span>
      </> :
      <span>{title}</span>;
    
  if (route)
    return (
      <Link
        href={route}
        className={`d-block text-decoration-none rounded ${dClassNAme} ${paddingClass} text-secondary`}
      >
        {textTitle}
      </Link>);
  
  if (hasChildren) {
    const buttonClass = level === 1
      ? `d-block`
      : `btn`;

    return (
      <>
        <button
          ref={btnRef}
          data-menu-toggle={id}
          type="button"
          onClick={() => toggleItem(id)}
          className={`d-flex w-100 text-start align-items-left justify-content-between border-0 ${dClassNAme} ${buttonClass} ${paddingClass}`}
        >
          {textTitle}
          <i className={`bi ${isExpanded ? "bi-chevron-up" : "bi-chevron-down"}`} />
        </button>
        {isExpanded && (() => {
          const children = sortEntries(Object.entries(_children)).map(([childTitle, childItem]) => (
            <SidebarItem
              key={`${id}>${childTitle}`}
              parentId={id}
              title={childTitle}
              item={childItem}
              level={level + 1}
              toggleItem={toggleItem}
              openItems={openItems}
            />
          ));

          const el = (
            <div
             ref={portalRef}
             data-menu-owner={id}
             data-menu-ancestors={parentId}
             className={level > 0 ? "submenu-float shadow mh-100 overflow-auto" : ""}
             style={(level > 0 && submenuPos) ? { position: "fixed", top: submenuPos.top + "px", left: submenuPos.left + "px", zIndex: 2147483647, pointerEvents: 'auto' } : undefined}
           >
              {children}
            </div>
          );

          // For nested floating submenus, portal them into document.body so they escape stacking contexts
          if (level > 0 && typeof document !== "undefined") {
            return createPortal(el, document.body);
          }

          return el;
        })()}
      </>
    );
  }

  return <div className={` d-block rounded ${dClassNAme} ${paddingClass} text-secondary`}>
    {textTitle}
  </div>;
};

const sortEntries = (
  entries: [string, MenuItemType][]
): [string, MenuItemType][] =>
  entries
    .slice()
    .sort(([a, itemA], [b, itemB]) => {
      const indexA = typeof itemA?.index === "number" ? itemA.index : Number.MAX_SAFE_INTEGER;
      const indexB = typeof itemB?.index === "number" ? itemB.index : Number.MAX_SAFE_INTEGER;
      return indexA - indexB || a.localeCompare(b);
    });

export default function Sidebar() {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const toggleItem = (id: string) => {
    setOpenItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const menuList = sortEntries(Object.entries(menu))
    .filter(([title]) => title !== "Settings")
    .map(([title, item]) => (
      <SidebarItem key={title} title={title} item={item} toggleItem={toggleItem} openItems={openItems} />
    ));

  return (
    <aside
      className="bg-white border-end d-flex flex-column position-fixed top-0 start-0 vh-100"
      style={{ width: 270 }}
    >
      <div className="p-4">
        <h2 className="fw-bold m-0">
          <i className="bi bi-triangle-fill me-2" />
          Bramacc
        </h2>
      </div>

      <SidebarItem item={{ icon: "bi-grid", route: ["/dashboard"] }} title="Overview" toggleItem={toggleItem} openItems={openItems} />
      <SidebarItem item={{ icon: "bi-person-badge" }} title="Contacts" toggleItem={toggleItem} openItems={openItems}  />

      <div className="overflow-auto">
        {menuList}
        <SidebarItem item={{ icon: "bi-grid-3x3-gap" }} title="Integration" toggleItem={toggleItem} openItems={openItems} />
        <SidebarItem item={{ icon: "bi-list-task" }} title="Tasks" toggleItem={toggleItem} openItems={openItems} />
      </div>

      <div className="border-top py-3">
        <SidebarItem item={menu.Settings} title="Settings" toggleItem={toggleItem} openItems={openItems} />
        <SidebarItem item={{ icon: "bi-question-circle" }} title="Help & Support" toggleItem={toggleItem} openItems={openItems} />
      </div>
    </aside>
  );
}



